import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import {
  PessoaDataService,
  ServicoDataService,
} from '@nomoh-monorepo/data-access-shared-services';
import { removerEspacos } from '@nomoh-monorepo/shared-angular-util-tools';
import { EstadosConst, IPessoa } from '@nomoh-monorepo/shared-types';
import {
  formatarCNPJ,
  formatarCPF,
  stringApenasNumeros,
  validarCNPJ,
  validarCPF,
} from '@nomoh-monorepo/shared-util-tools';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { SelectModule } from 'primeng/select';
import { AppConfig } from '../../auth/models/app-config.interface';
import { FormFieldComponent } from '../../form-field/form-field.component';
import { NMultiselectComponent } from '../../n-multiselect/n-multiselect.component';
import { PhoneInputComponent } from '../../phone-input/phone-input.component';

@Component({
  selector: 'lib-cadastrar',
  imports: [
    CommonModule,
    FormFieldComponent,
    TranslateModule,
    FileUploadModule,
    ReactiveFormsModule,
    ButtonModule,
    PasswordModule,
    SelectModule,
    InputTextModule,
    DatePickerModule,
    NMultiselectComponent,
    PhoneInputComponent,
  ],
  standalone: true,
  templateUrl: './cadastrar.component.html',
  styleUrl: './cadastrar.component.scss',
})
export class CadastrarComponent implements OnInit {
  form!: FormGroup;
  isLoading = signal<boolean>(false);
  isMobileView = signal<boolean>(
    typeof window !== 'undefined'
      ? window.matchMedia('(max-width: 768px)').matches
      : false
  );
  cadastroConcluido = signal(false);
  emailCadastrado = signal('');
  nomeCadastrado = signal('');
  filePreviewUrl: string | undefined = undefined;

  // Injeção de dependências
  private fb = inject(FormBuilder);
  private pessoaDataService = inject(PessoaDataService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private notificacaoService = inject(NotificacaoService);
  private servicoDataService = inject(ServicoDataService);

  // Configuração do app (sistemaId para associação automática de plano)
  private appConfig: AppConfig | null = null;

  // Validação de senha
  hasLowerCase = signal<boolean>(false);
  hasUpperCase = signal<boolean>(false);
  hasNumber = signal<boolean>(false);
  hasSpecialChar = signal<boolean>(false);
  hasMinLength = signal<boolean>(false);
  regexSenha = '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})';

  get senhaValida(): boolean {
    return (
      this.hasLowerCase() &&
      this.hasUpperCase() &&
      this.hasNumber() &&
      this.hasSpecialChar() &&
      this.hasMinLength()
    );
  }
  get senhasIguais(): boolean {
    const senha = this.form.get('seguranca.senha')?.value;
    const confirmarSenha = this.form.get('seguranca.confirmarSenha')?.value;
    return senha && confirmarSenha && senha === confirmarSenha;
  }

  hoje = new Date();
  estados = EstadosConst.sigla;
  sexoOpcoes = [
    { label: 'Não informado', value: 0 },
    { label: 'Masculino', value: 1 },
    { label: 'Feminino', value: 2 },
  ];

  ngOnInit() {
    // Ler appConfig da rota para obter sistemaId
    this.appConfig = this.route.snapshot.data['appConfig'] || null;

    this.criarFormulario();
    this.configurarValidacaoSenha();
  }

  criarFormulario() {
    this.form = this.fb.group({
      nomeCompleto: ['', [Validators.required, this.nomeSobrenomeValidator]],
      cpfCnpj: ['', [this.cpfCnpjValidator]],
      email: ['', [Validators.required, Validators.email]],
      telefone: ['', Validators.required],
      sexo: [0],
      dataNascimento: [null, Validators.required],
      imagemPerfil: [''],
      typeImage: [''],
      seguranca: this.fb.group(
        {
          senha: [
            '',
            [
              Validators.required,
              Validators.minLength(6),
              this.validadorSenhaCompleta,
            ],
          ],
          confirmarSenha: ['', Validators.required],
        },
        { validators: this.validadorSenhasIguais }
      ),
      endereco: this.fb.group({
        codPostal: [''],
        logradouro: [''],
        numero: [''],
        complemento: [''],
        bairro: [''],
        cidade: [''],
        estado: [''],
        pais: ['Brasil'],
        latitude: [''],
        longitude: [''],
      }),
    });
  }

  onTelefoneChange(value: string): void {
    const ctrl = this.form.get('telefone');
    ctrl?.setValue(value);
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
  }

  // Validações customizadas
  nomeSobrenomeValidator(control: any) {
    const nomeCompleto = control.value?.trim();
    if (!nomeCompleto || nomeCompleto.split(' ').length < 2) {
      return { nomeIncompleto: true };
    }
    return null;
  }

  cpfCnpjValidator(control: any) {
    const valor = control.value;
    if (!valor) return null;

    const limpo = stringApenasNumeros(valor);
    if (!validarCPF(limpo) && !validarCNPJ(limpo)) {
      return { cpfCnpjInvalido: true };
    }
    return null;
  }

  // Formatação de CPF/CNPJ
  formatarCpfCnpj(event: Event) {
    const input = event.target as HTMLInputElement;
    const valor = stringApenasNumeros(input.value);

    if (valor.length <= 11) {
      input.value = formatarCPF(valor);
    } else {
      input.value = formatarCNPJ(valor);
    }

    this.form.get('cpfCnpj')?.setValue(input.value);
  }

  // Busca de CEP
  async buscarCep(event: Event) {
    const input = event.target as HTMLInputElement;
    const cep = stringApenasNumeros(input.value);
    if (cep.length === 8) {
      this.servicoDataService.buscarCep(cep).then((data: any) => {
        if (data && !data.erro) {
          this.form.patchValue({
            endereco: {
              logradouro: data.logradouro || '',
              bairro: data.bairro || '',
              cidade: data.localidade || '',
              estado: data.uf || '',
              pais: 'Brasil',
            },
          });
        }
      });
    }
  }

  // Upload de imagem
  imagemSelecionada(event: any, fileUpload: any): void {
    const file = event.files[0];

    if (file) {
      // Validar tamanho e tipo
      if (file.size > 10000000) {
        // 10MB
        this.notificacaoService.notificacaoWarn(
          'Arquivo muito grande. Máximo 10MB.'
        );
        fileUpload.clear();
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        this.form.patchValue({
          imagemPerfil: base64String.split(',')[1],
          typeImage: file.type,
        });
        this.filePreviewUrl = base64String;
      };

      reader.readAsDataURL(file);
    }

    fileUpload.clear();
  }

  removerImagem() {
    this.filePreviewUrl = undefined;
    this.form.patchValue({
      imagemPerfil: '',
      typeImage: '',
    });
  }
  // Submissão do formulário
  salvar() {
    removerEspacos(this.form);
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.notificacaoService.notificacaoWarn(
        this.translate.instant('PAGINA_CADASTRO.MENSAGEM_ERROS_FORMULARIO')
      );
      return;
    }

    this.isLoading.set(true);

    const usuario = this.montarUsuario();

    // Passa sistemaId para associação automática de plano (se configurado na rota)
    const sistemaId = this.appConfig?.sistema;

    this.pessoaDataService
      .adicionarUsuario(usuario, sistemaId)
      .then(() => {
        const primeiroNome = usuario.nome.split(' ')[0];
        this.emailCadastrado.set(usuario.email ?? '');
        this.nomeCadastrado.set(primeiroNome);
        this.cadastroConcluido.set(true);
      })
      .catch((error: unknown) => {
        console.error('Erro ao cadastrar usuário:', error);
      })
      .finally(() => {
        this.isLoading.set(false);
      });
  }

  private montarUsuario(): IPessoa {
    const formValue = this.form.value;
    const cpf = stringApenasNumeros(formValue.cpfCnpj || '');
    const endereco = formValue.endereco;
    const temEndereco = !!(
      stringApenasNumeros(endereco?.codPostal || '') ||
      endereco?.logradouro ||
      endereco?.cidade
    );

    return {
      nome: formValue.nomeCompleto,
      ...(cpf ? { cpfCnpj: cpf } : {}),
      email: formValue.email,
      telefone: formValue.telefone || null,
      sexo: formValue.sexo ?? null,
      dataNascimento: formValue.dataNascimento ?? null,
      idioma: null,
      imagemPerfil: formValue.imagemPerfil || '',
      typeImage: formValue.typeImage || '',
      status: formValue.status || 'ativo',
      seguranca: {
        senha: formValue.seguranca.senha,
        confirmacaoSenha: formValue.seguranca.confirmarSenha,
      },
      ...(temEndereco
        ? {
            endereco: {
              logradouro: endereco.logradouro,
              numero: endereco.numero,
              bairro: endereco.bairro,
              cidade: endereco.cidade,
              estado: endereco.estado,
              complemento: endereco.complemento || '',
              pais: endereco.pais || 'Brasil',
              codPostal: stringApenasNumeros(endereco.codPostal),
              latitude: null,
              longitude: null,
            },
          }
        : {}),
    };
  }

  cancelar() {
    this.router.navigate(['/login']);
  }

  navegarHome(): void {
    this.router.navigateByUrl(this.appConfig?.dashboardRoute || '/');
  }

  private configurarValidacaoSenha(): void {
    // Monitora mudanças na senha para validação em tempo real
    this.form
      .get('seguranca.senha')
      ?.valueChanges.subscribe((senha: string) => {
        this.validarRequisitosSenha(senha);
      });

    // Monitora mudanças na confirmação de senha
    this.form.get('seguranca.confirmarSenha')?.valueChanges.subscribe(() => {
      this.validarConfirmacaoSenha();
    });
  }

  // ========================================
  // VALIDADORES CUSTOMIZADOS
  // ========================================
  private validadorSenhasIguais = (formGroup: FormGroup) => {
    // Como este validador está aplicado ao FormGroup 'seguranca',
    // deve acessar os campos diretamente (sem o prefixo 'seguranca.')
    const senha = formGroup.get('senha')?.value;
    const confirmarSenha = formGroup.get('confirmarSenha')?.value;

    if (senha && confirmarSenha && senha !== confirmarSenha) {
      formGroup.get('confirmarSenha')?.setErrors({ passwordsMismatch: true });
      return { passwordsMismatch: true };
    }

    return null;
  };

  // ========================================
  // MÉTODOS DE VALIDAÇÃO
  // ========================================
  private validarRequisitosSenha(senha: string): void {
    if (!senha) {
      this.resetarValidacaoSenha();
      return;
    }

    this.hasLowerCase.set(/[a-z]/.test(senha));
    this.hasUpperCase.set(/[A-Z]/.test(senha));
    this.hasNumber.set(/[0-9]/.test(senha));
    this.hasSpecialChar.set(/[!@#$%^&*]/.test(senha));
    this.hasMinLength.set(senha.length >= 8);
  }

  private resetarValidacaoSenha(): void {
    this.hasLowerCase.set(false);
    this.hasUpperCase.set(false);
    this.hasNumber.set(false);
    this.hasSpecialChar.set(false);
    this.hasMinLength.set(false);
  }

  private validarConfirmacaoSenha(): void {
    const senha = this.form.get('seguranca.senha')?.value;
    const confirmarSenha = this.form.get('seguranca.confirmarSenha')?.value;

    if (confirmarSenha && senha !== confirmarSenha) {
      this.form
        .get('seguranca.confirmarSenha')
        ?.setErrors({ passwordsMismatch: true });
      // Também marca o FormGroup como inválido
      this.form.get('seguranca')?.setErrors({ passwordsMismatch: true });
    } else if (confirmarSenha && senha === confirmarSenha) {
      // Remove o erro de senhas diferentes
      const errors = this.form.get('seguranca.confirmarSenha')?.errors;
      if (errors) {
        delete errors['passwordsMismatch'];
        const hasOtherErrors = Object.keys(errors).length > 0;
        this.form
          .get('seguranca.confirmarSenha')
          ?.setErrors(hasOtherErrors ? errors : null);
      }
      // Limpa o erro do FormGroup também
      const groupErrors = this.form.get('seguranca')?.errors;
      if (groupErrors) {
        delete groupErrors['passwordsMismatch'];
        const hasOtherGroupErrors = Object.keys(groupErrors).length > 0;
        this.form
          .get('seguranca')
          ?.setErrors(hasOtherGroupErrors ? groupErrors : null);
      }
    }
  }
  private validadorSenhaCompleta = (control: any) => {
    const senha = control.value;
    if (!senha) return null;

    this.validarRequisitosSenha(senha);

    if (!this.senhaValida) {
      return { senhaIncompleta: true };
    }

    return null;
  };

  get placeholderNomeCompleto() {
    return this.translate.instant('PAGINA_CADASTRO.PLACEHOLDER_NOME_COMPLETO');
  }
  get placeholderEmail() {
    return this.translate.instant('PAGINA_CADASTRO.PLACEHOLDER_EMAIL');
  }
  get placeholderLogradouro() {
    return this.translate.instant('PAGINA_CADASTRO.PLACEHOLDER_LOGRADOURO');
  }
  get placeholderComplemento() {
    return this.translate.instant('PAGINA_CADASTRO.PLACEHOLDER_COMPLEMENTO');
  }
  get placeholderBairro() {
    return this.translate.instant('PAGINA_CADASTRO.PLACEHOLDER_BAIRRO');
  }
  get placeholderCidade() {
    return this.translate.instant('PAGINA_CADASTRO.PLACEHOLDER_CIDADE');
  }
  get placeholderEstado() {
    return this.translate.instant('PAGINA_CADASTRO.PLACEHOLDER_ESTADO');
  }
  get placeholderSenha() {
    return this.translate.instant('PAGINA_CADASTRO.PLACEHOLDER_SENHA');
  }
  get placeholderConfirmarSenha() {
    return this.translate.instant(
      'PAGINA_CADASTRO.PLACEHOLDER_CONFIRMAR_SENHA'
    );
  }

  get placeHolderBotaoImagem() {
    return this.translate.instant('PAGINA_CADASTRO.PLACEHOLDER_BOTAO_IMAGEM');
  }
}
